import { DBFunctionReturns } from '../../custom_types'
import { differenceInCalendarDays } from 'date-fns'
import { parseDateValue } from '@dolfin/utils/dates'
import { batched } from '@dolfin/utils/iterators'
import { Tables } from '../../db_types'
import {
  aggregateCreditedMetricValues,
  resolveCreditedConsumerRuleCoverage
} from '../credited_consumers'
import { getRecordFieldValue } from '../../record'
import { resolveMetricDependencyGraph } from '../metrics/references'
import type { MetricSourceRecord } from '../metrics'
import { DolfinDiagram, type Diagram } from '../../diagram'
import { getPayCurveMetrics } from '../../pay-curves/calculations'
import type { PayCurveConfig } from '../../pay-curves/types'
import type { AnyTypedNode } from '../../node/types'
import { z } from 'zod'
import type {
  DolfinRpc,
  TargetContributingSourceRecord,
  TargetContributingRecordsParams,
  TargetContributingRecordsResult,
  TargetContributingSourceRecordsPageParams,
  TargetAchievementRow,
  TargetCreditSetupSelectionParams,
  TargetUpsertWithCreditSetupSelectionParams,
  TargetCreditedEvidenceSummary,
  GetUsersRankingForTargetParams,
  TargetCumulativeAchievementRow,
  UsersRankingForTargetRow,
  TargetCreditSetupSegment
} from '../../rpc'

export type TargetCurrencyMode = 'organization' | 'team' | 'custom'

export type UserViewTargetSummaryRow = TargetAchievementRow & {
  aggregate_function?: Tables<'metrics'>['aggregate_function'] | null
  target_team_ids?: number[] | null
}

export type TargetRankingRow = UsersRankingForTargetRow & {
  credit_setup_id: number | null
  creditedSegments?: TargetCreditSetupSegment[]
}

export type TargetAchievementSeriesRow = TargetCumulativeAchievementRow & {
  creditedSegments?: TargetCreditSetupSegment[]
}

const TARGET_CREDIT_SETUP_SEGMENT_PROFILE_BATCH_SIZE = 100

export function getTargetCurrency({
  currencyMode,
  targetCurrency,
  orgCurrency,
  teamId,
  teamCurrency
}: {
  currencyMode?: TargetCurrencyMode | null
  targetCurrency?: string | null
  orgCurrency?: string
  teamId?: number
  teamCurrency?: string
}): string {
  if (currencyMode === 'custom' && targetCurrency) {
    return targetCurrency
  }

  if (currencyMode === 'team' && teamId != null && teamCurrency) {
    return teamCurrency
  }

  return orgCurrency ?? 'USD'
}

export type TargetUsage = {
  compensationRules: (Tables<'compensation_rules'> & {
    compensation_rule_versions: (Tables<'compensation_rule_versions'> & {
      compensation_rule_version_targets: Tables<'compensation_rule_version_targets'>[]
    })[]
  })[]
  isBeingUsed: boolean
}

type TargetWithMetric = Tables<'targets'> & {
  metrics: Tables<'metrics'>
}

export type TargetContributingRecordsContext = {
  target: TargetWithMetric
  requesterId: string
  displayedProfileId: string
  organizationId: number
  periodStart: string
  periodEnd: string
  targetAchievement: NonNullable<TargetContributingRecordsResult['targetAchievement']>
  isAdmin: boolean
  effectiveTeamId: number | null
}

export class TargetContributingRecordsDomainError extends Error {
  constructor(
    readonly kind: 'bad_request' | 'forbidden' | 'not_found',
    message: string
  ) {
    super(message)
  }
}

export class TargetCreditSetupSelectionValidationError extends Error {}

export class TargetCreditSetupSelectionNotFoundError extends Error {}

export const canSelectCreditSetupForSource = (source: string | null | undefined) =>
  source != null && source !== 'custom_record'

export const parseTargetCreditSetupSelectionPayload = (
  value: unknown
): Pick<TargetCreditSetupSelectionParams, 'creditSetupId'> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TargetCreditSetupSelectionValidationError('Request body must be a JSON object')
  }

  if (!Object.prototype.hasOwnProperty.call(value, 'credit_setup_id')) {
    throw new TargetCreditSetupSelectionValidationError(
      'credit_setup_id is required and may be null'
    )
  }

  const creditSetupId = (value as { credit_setup_id?: unknown }).credit_setup_id
  if (
    creditSetupId !== null &&
    (typeof creditSetupId !== 'number' || !Number.isSafeInteger(creditSetupId) || creditSetupId < 1)
  ) {
    throw new TargetCreditSetupSelectionValidationError(
      'credit_setup_id must be a positive integer or null'
    )
  }

  return { creditSetupId }
}

type TargetAchievementSummaryRow = Pick<
  DBFunctionReturns<'get_user_targets_achievement'>[number],
  'period_start' | 'period_end'
>

const getPeriodDurationDays = ({ period_start, period_end }: TargetAchievementSummaryRow) => {
  const start = parseDateValue(period_start)
  const end = parseDateValue(period_end)

  if (!start || !end) {
    return 0
  }

  return differenceInCalendarDays(end, start) + 1
}

export const sortUserTargetsAchievementRows = <TRow extends TargetAchievementSummaryRow>(
  targets: TRow[] | null
) =>
  targets
    ? [...targets].sort((left, right) => getPeriodDurationDays(left) - getPeriodDurationDays(right))
    : null

type CompletedTargetAttainmentFact =
  DBFunctionReturns<'get_organization_completed_target_attainment'>[number]

type CurveCandidate = {
  versionId: number
  nodeId: string
  payoutUnit: 'commission' | 'bonus'
  config: PayCurveConfig
  capTargetAchievement: boolean
}

export type QuotaSignal =
  | 'insufficient_evidence'
  | 'quota_may_be_aggressive'
  | 'quota_may_be_easy'
  | 'within_configured_range'
  | 'unsupported'

export type CompletedTargetAttainmentDataStatus =
  | 'eligible'
  | 'no_eligible_participants'
  | 'redacted_small_cohort'
  | 'requires_materialized_annual_fact'
  | 'shared_target_single_observation'
  | 'unsupported_target_type'
  | 'unsupported_upper_bound_target'
export type CompletedTargetCurveCohort = {
  ruleVersionId: number
  nodeId: string
  payoutUnit: 'commission' | 'bonus'
  sampleSize: number
  coverageRate: number
  curveStatus: 'eligible' | 'insufficient_evidence' | 'unresolved_payout_formula'
  thresholdAttainment: number | null
  acceleratorAttainment: number | null
  capAttainment: number | null
  thresholdHitRate: number | null
  acceleratorParticipationRate: number | null
  capParticipationRate: number | null
}

export type CompletedTargetAttainmentAnalysis = {
  targetId: number
  targetLabel: string
  periodStart: string
  periodEnd: string
  targetType: CompletedTargetAttainmentFact['target_type']
  targetScope: string
  dataStatus: CompletedTargetAttainmentDataStatus
  sampleSize: number
  eligibleParticipantCount: number
  excludedParticipantCount: number
  p25Attainment: number | null
  medianAttainment: number | null
  p75Attainment: number | null
  quotaAttainmentRate: number | null
  lowerTailRate: number | null
  upperTailRate: number | null
  quotaSignal: QuotaSignal
  governanceStatus:
    | 'not_evaluated'
    | 'no_applicable_curve'
    | 'multiple_applicable_curves'
    | 'single_applicable_curve'
  curveEligibilityCoverage: number | null
  curveCohorts: CompletedTargetCurveCohort[]
  scoreVersion: null
  scoreComponents: null
}

const minimumDisclosureCohortSize = 5

const percentile = (values: number[], quantile: number): number | null => {
  if (values.length === 0) return null

  const sorted = [...values].sort((left, right) => left - right)
  const index = (sorted.length - 1) * quantile
  const lower = Math.floor(index)
  const upper = Math.ceil(index)

  if (lower === upper) return sorted[lower]
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower)
}

const DiagramJsonSchema = z
  .object({
    nodes: z.array(
      z
        .object({
          id: z.string(),
          type: z.string().nullable().optional(),
          data: z.object({ formData: z.unknown() }).passthrough()
        })
        .passthrough()
    ),
    edges: z.array(
      z
        .object({
          source: z.string(),
          target: z.string()
        })
        .passthrough()
    )
  })
  .passthrough()

const TieredTargetFormDataSchema = z.object({
  type: z.literal('tiered'),
  reference_type: z.literal('target'),
  targets: z.array(z.object({ id: z.number() }).passthrough()).length(1),
  tiers: z
    .array(
      z
        .object({
          attainment: z.number().finite(),
          value: z.union([z.number().finite(), z.string()]).optional(),
          percentage: z.union([z.number().finite(), z.string()]).optional()
        })
        .passthrough()
    )
    .min(1),
  cap_target_achievement: z.boolean().optional()
})

const asDiagram = (value: unknown): Diagram | null => {
  const parsed = DiagramJsonSchema.safeParse(value)
  if (!parsed.success) return null

  // `DolfinDiagram` accepts the validated persisted node/edge shape. Its richer
  // React Flow types carry editor-only fields that are not required for analysis.
  const diagram = parsed.data as unknown as Diagram
  return diagram
}

const getTargetTierCandidates = (
  diagramValue: unknown,
  targetId: number,
  versionId: number
): CurveCandidate[] => {
  const diagramValueTyped = asDiagram(diagramValue)
  if (!diagramValueTyped) return []

  const diagram = new DolfinDiagram(diagramValueTyped)
  const startNode = diagram.getStartNode()
  if (!startNode) return []

  const paths: Array<{ node: AnyTypedNode; conditional: boolean }> = [
    { node: startNode, conditional: false }
  ]
  const visited = new Set<string>()
  const candidates: CurveCandidate[] = []

  while (paths.length > 0) {
    const current = paths.pop()!
    const visitKey = `${current.node.id}:${current.conditional}`
    if (visited.has(visitKey)) continue
    visited.add(visitKey)

    const outPaths = diagram.outPaths(current.node)
    const nextConditional =
      current.conditional ||
      current.node.type === 'if/else' ||
      current.node.type === 'filter' ||
      outPaths.length > 1

    for (const edge of outPaths) {
      const nextNode = diagram.getNextNode(edge)
      if (nextNode) {
        paths.push({ node: nextNode, conditional: nextConditional })
      }
    }

    if (
      current.conditional ||
      (current.node.type !== 'commission' && current.node.type !== 'bonus')
    ) {
      continue
    }

    if (!('formData' in current.node.data)) {
      continue
    }

    const parsedFormData = TieredTargetFormDataSchema.safeParse(current.node.data?.formData)
    if (!parsedFormData.success || parsedFormData.data.targets[0].id !== targetId) {
      continue
    }

    const points = parsedFormData.data.tiers.flatMap(tier => {
      const payout =
        tier.value ??
        (typeof tier.percentage === 'number' ? tier.percentage * 100 : tier.percentage)
      return payout == null ? [] : [{ attainment: tier.attainment, payout }]
    })

    candidates.push({
      versionId,
      nodeId: current.node.id,
      payoutUnit: current.node.type,
      config: { points },
      capTargetAchievement: parsedFormData.data.cap_target_achievement === true
    })
  }

  return candidates
}
export default class Targets {
  private rpcClient: DolfinRpc
  private readonly target?: Tables<'targets'>

  constructor({ rpcClient, target }: { rpcClient: DolfinRpc; target?: Tables<'targets'> }) {
    this.rpcClient = rpcClient
    this.target = target
  }

  getTargetUsage(): Promise<TargetUsage> {
    if (!this.target) {
      throw new Error('Target is not defined')
    }
    return this.rpcClient.getTargetUsage(this.target.id)
  }

  async setCreditSetupSelection(params: TargetCreditSetupSelectionParams) {
    if (!Number.isSafeInteger(params.targetId) || params.targetId < 1) {
      throw new TargetCreditSetupSelectionValidationError('targetId must be a positive integer')
    }

    if (
      params.creditSetupId !== null &&
      (!Number.isSafeInteger(params.creditSetupId) || params.creditSetupId < 1)
    ) {
      throw new TargetCreditSetupSelectionValidationError(
        'creditSetupId must be a positive integer or null'
      )
    }

    // Clearing the selection must remain a direct, null-valued RPC write. It
    // does not need to inspect the current Target or Metric configuration.
    if (params.creditSetupId === null) {
      return this.rpcClient.setTargetCreditSetupSelection(params)
    }

    const target = await this.rpcClient.getTarget(params.targetId)
    if (!target) {
      throw new TargetCreditSetupSelectionNotFoundError('Target not found')
    }

    if (
      target.is_manual ||
      target.metric_id == null ||
      !canSelectCreditSetupForSource(target.metrics?.source)
    ) {
      throw new TargetCreditSetupSelectionValidationError(
        'Only automatic Targets backed by source Metrics can select a Credit Setup'
      )
    }

    return this.rpcClient.setTargetCreditSetupSelection(params)
  }

  async upsertWithCreditSetupSelection(params: TargetUpsertWithCreditSetupSelectionParams) {
    if (
      params.target.organization_id == null ||
      !Number.isSafeInteger(params.target.organization_id) ||
      params.target.organization_id < 1
    ) {
      throw new TargetCreditSetupSelectionValidationError(
        'organization_id must be a positive integer'
      )
    }

    if (
      params.target.id != null &&
      (!Number.isSafeInteger(params.target.id) || params.target.id < 1)
    ) {
      throw new TargetCreditSetupSelectionValidationError(
        'id must be a positive integer when updating a Target'
      )
    }

    if (
      params.creditSetupId !== null &&
      (!Number.isSafeInteger(params.creditSetupId) || params.creditSetupId < 1)
    ) {
      throw new TargetCreditSetupSelectionValidationError(
        'creditSetupId must be a positive integer or null'
      )
    }

    return this.rpcClient.upsertTargetWithCreditSetupSelection(params)
  }

  async applyCreditedUserViewSummary({
    rows,
    profileId,
    startDate,
    endDate,
    organizationId
  }: {
    rows: UserViewTargetSummaryRow[]
    profileId: string
    startDate: string
    endDate: string
    organizationId: number
  }): Promise<UserViewTargetSummaryRow[]> {
    const creditedRows = rows.filter(
      row =>
        row.credit_setup_id != null &&
        row.metric_id != null &&
        row.period_start != null &&
        row.period_end != null &&
        row.period_end >= startDate &&
        row.period_start <= endDate &&
        row.aggregate_function != null
    )

    if (creditedRows.length === 0) return rows

    const assignmentsBySetupPromise = Promise.all(
      Array.from(new Set(creditedRows.map(row => row.credit_setup_id!))).map(
        async creditSetupId =>
          [
            creditSetupId,
            await this.rpcClient.listCreditSetupRuleAssignments({
              organizationId,
              creditSetupId
            })
          ] as const
      )
    ).then(entries => new Map(entries))

    const creditedTargetTeamIds = Array.from(
      new Set(creditedRows.flatMap(row => row.target_team_ids ?? []))
    )
    const creditedStartDate = creditedRows.reduce(
      (earliestStart, row) =>
        row.period_start! < earliestStart ? row.period_start! : earliestStart,
      startDate
    )
    const profileTeamMembershipsPromise = this.rpcClient
      .getTeamMembershipsInPeriod({
        end: endDate,
        organizationId,
        start: creditedStartDate,
        teamIds: creditedTargetTeamIds
      })
      .then(memberships =>
        memberships
          .filter(membership => membership.profileId === profileId)
          .map(membership => ({
            effectiveFrom: membership.activeFrom,
            effectiveTo: membership.activeUntil,
            teamId: membership.teamId
          }))
      )
    const fallbackTeamByDate = new Map<string, Promise<Tables<'teams'> | null>>()

    const requests = await Promise.all(
      creditedRows.map(async row => {
        let teamIds = row.target_team_ids ?? []
        let teamMemberships = [] as {
          teamId: number
          effectiveFrom: string
          effectiveTo: string | null
        }[]

        const profileTeamMemberships = await profileTeamMembershipsPromise
        teamMemberships = profileTeamMemberships.filter(
          membership =>
            !(row.team_target || row.global_target || row.company_target) ||
            teamIds.length === 0 ||
            teamIds.includes(membership.teamId)
        )
        if (row.team_target || row.global_target || row.company_target) {
          teamIds = [...new Set(teamMemberships.map(membership => membership.teamId))]
        }
        if (row.team_target && !row.global_target && !row.company_target && teamIds.length === 0) {
          const fallbackKey = `${organizationId}:${profileId}:${row.period_end!}`
          let teamPromise = fallbackTeamByDate.get(fallbackKey)
          if (!teamPromise) {
            teamPromise = this.rpcClient.getTeam(profileId, organizationId, row.period_end!)
            fallbackTeamByDate.set(fallbackKey, teamPromise)
          }
          const team = await teamPromise
          teamIds = team ? [team.id] : []
        }

        const requestStartDate = row.period_start!
        const requestEndDate = row.period_end! < endDate ? row.period_end! : endDate
        const resolverTeamMemberships =
          profileTeamMemberships.length > 0
            ? teamMemberships
            : teamIds.map(teamId => ({
                teamId,
                effectiveFrom: requestStartDate,
                effectiveTo: null
              }))
        const assignmentsBySetup = await assignmentsBySetupPromise
        const ruleSegments = resolveCreditedConsumerRuleCoverage({
          assignments: assignmentsBySetup.get(row.credit_setup_id!) ?? [],
          creditSetupId: row.credit_setup_id!,
          profileId,
          teamMemberships: resolverTeamMemberships,
          startDate: requestStartDate,
          endDate: requestEndDate
        }).flatMap(segment =>
          segment.resolution
            ? [
                {
                  creditSetupRuleId: segment.resolution.ruleId,
                  effectiveFrom: segment.effectiveFrom,
                  effectiveTo: segment.effectiveTo
                }
              ]
            : []
        )

        return {
          organizationId,
          metricId: row.metric_id!,
          creditSetupId: row.credit_setup_id!,
          callSite: 'target_summary',
          aggregateFunction: row.aggregate_function ?? undefined,
          ruleSegments,
          profileId,
          teamIds,
          ...(profileTeamMemberships.length > 0 ? { teamMemberships } : {}),
          // A Credit Setup assignment determines the beneficiary and eligible
          // source values. Applying the Target's direct profile/team scopes
          // would drop credits earned from records owned by other people/teams.
          valueProfileIds: null,
          valueTeamIds: undefined,
          targetCurrency: row.target_currency ?? undefined,
          // Target-card achievement is cumulative from the configured Target
          // period start through the selected month. Unlike the direct summary
          // RPC, credited values are resolved here, so do not narrow this to
          // the selected calendar month.
          startDate: requestStartDate,
          endDate: requestEndDate
        }
      })
    )
    const creditedAggregates = await this.rpcClient.getCreditedTargetMetricValueAggregates(requests)

    return rows.map(row => {
      const requestIndex = creditedRows.indexOf(row)
      if (requestIndex === -1 || row.aggregate_function == null) return row

      const totalAchievement = Number(
        creditedAggregates.find(value => Number(value.request_id) === requestIndex)?.value ?? 0
      )

      return {
        ...row,
        total_achievement: totalAchievement,
        total_achievement_percentage: Targets.computeTargetAchievement(
          Number(row.total_period_amount ?? 0),
          Boolean(row.is_upper_bound),
          Boolean(row.allow_partial_outcome),
          totalAchievement
        )
      }
    })
  }

  async getPeriodTargetAmount(targetId: number, start: string, end: string, profileId: string) {
    return this.rpcClient.getPeriodTargetAmount(targetId, start, end, profileId)
  }

  async getMissingAmounts(organizationId: number) {
    return this.rpcClient.getTargetsMissingAmounts(organizationId)
  }

  async getCumulativeAchievement({
    targetId,
    organizationId,
    profileId,
    startDate,
    endDate,
    groupBy
  }: {
    targetId: number
    organizationId: number
    profileId: string
    startDate: string
    endDate: string
    groupBy: string
  }): Promise<TargetAchievementSeriesRow[]> {
    const target = await this.rpcClient.getTarget(targetId)
    if (!target || target.organization_id !== organizationId) {
      throw new TargetCreditSetupSelectionNotFoundError('Target not found')
    }

    if (target.credit_setup_id == null) {
      return this.rpcClient.getCumulativePeriodTargetAchievement(
        targetId,
        startDate,
        endDate,
        profileId,
        groupBy
      )
    }

    const creditedSegments = await this.rpcClient.getTargetCreditSetupSegments({
      targetId,
      profileIds: [profileId],
      startDate,
      endDate
    })

    const rows = await this.rpcClient.getCreditedTargetAchievementSeries({
      targetId,
      profileId,
      startDate,
      endDate,
      groupBy,
      ruleSegments: creditedSegments.flatMap(segment =>
        segment.creditSetupRuleId == null
          ? []
          : [
              {
                creditSetupRuleId: segment.creditSetupRuleId,
                effectiveFrom: segment.effectiveFrom,
                effectiveTo: segment.effectiveTo
              }
            ]
      )
    })

    return rows.map(row => ({ ...row, creditedSegments }))
  }

  async getRankings({
    targetId,
    organizationId,
    startDate,
    endDate,
    order,
    tags,
    teamId
  }: GetUsersRankingForTargetParams): Promise<TargetRankingRow[]> {
    const target = await this.rpcClient.getTarget(targetId)
    if (!target || target.organization_id !== organizationId) {
      throw new TargetCreditSetupSelectionNotFoundError('Target not found')
    }

    const rankings = await this.rpcClient.getUsersRankingForTarget({
      targetId,
      organizationId,
      startDate,
      endDate,
      order,
      tags,
      teamId
    })

    if (target.credit_setup_id == null) {
      return rankings.map(ranking => ({ ...ranking, credit_setup_id: null }))
    }

    const segments = (
      await Promise.all(
        Array.from(
          batched(
            Array.from(new Set(rankings.map(ranking => ranking.profile_id))),
            TARGET_CREDIT_SETUP_SEGMENT_PROFILE_BATCH_SIZE
          )
        ).map(profileIds =>
          this.rpcClient.getTargetCreditSetupSegments({
            targetId,
            profileIds,
            startDate,
            endDate
          })
        )
      )
    ).flat()
    const segmentsByProfile = new Map<string, TargetCreditSetupSegment[]>()
    for (const segment of segments) {
      const profileSegments = segmentsByProfile.get(segment.profileId) ?? []
      profileSegments.push(segment)
      segmentsByProfile.set(segment.profileId, profileSegments)
    }

    return rankings.map(ranking => ({
      ...ranking,
      credit_setup_id: target.credit_setup_id,
      creditedSegments: segmentsByProfile.get(ranking.profile_id) ?? []
    }))
  }

  getContributingRecords(params: TargetContributingRecordsParams) {
    return this.rpcClient.getTargetContributingRecords(params)
  }

  getTeamIds(organizationId: number) {
    if (!this.target) throw new Error('Target is not defined')
    return this.rpcClient.getTargetTeamIds(this.target.id, organizationId)
  }

  async resolveContributingRecordsContext({
    targetId,
    requesterId,
    displayedProfileId,
    periodStart,
    periodEnd
  }: {
    targetId: number
    requesterId: string
    displayedProfileId: string
    periodStart: string
    periodEnd: string
  }): Promise<TargetContributingRecordsContext> {
    const target = await this.rpcClient.getTarget(targetId)

    if (!target) {
      throw new TargetContributingRecordsDomainError('not_found', 'Target not found')
    }

    if (!target.metrics) {
      throw new TargetContributingRecordsDomainError('bad_request', 'Target does not have a metric')
    }

    if (!target.team_target && !target.global_target && !target.company_target) {
      throw new TargetContributingRecordsDomainError(
        'bad_request',
        'Contributing-record visibility applies only to shared targets'
      )
    }

    const targetWithMetric: TargetWithMetric = {
      ...target,
      metrics: target.metrics
    }

    const organizationId = targetWithMetric.organization_id
    if (targetWithMetric.metrics.organization_id !== organizationId) {
      throw new TargetContributingRecordsDomainError(
        'not_found',
        'Target metric is not available in this organization'
      )
    }

    const access = await this.rpcClient.getTargetContributingRecordsAccess({
      requesterId,
      displayedProfileId,
      organizationId
    })

    if (!access.canAccess) {
      throw new TargetContributingRecordsDomainError(
        'forbidden',
        'You do not have permission to access this target profile'
      )
    }

    const targetAchievements = await this.rpcClient.getUserTargetsAchievementInPeriod(
      displayedProfileId,
      periodStart,
      periodEnd,
      organizationId,
      targetId
    )
    const targetAchievement = targetAchievements?.find(
      achievement => achievement.target_id === targetId && achievement.period_start === periodStart
    )

    if (!targetAchievement) {
      throw new TargetContributingRecordsDomainError(
        'not_found',
        'Target is not available for this profile and period'
      )
    }

    const effectiveTeam = targetWithMetric.team_target
      ? await this.rpcClient.getTeam(displayedProfileId, organizationId, periodEnd)
      : null

    return {
      target: targetWithMetric,
      requesterId,
      displayedProfileId,
      organizationId,
      periodStart,
      periodEnd,
      targetAchievement,
      isAdmin: access.isAdmin,
      effectiveTeamId: effectiveTeam?.id ?? null
    }
  }

  async getAuthorizedContributingRecords({
    context,
    requestedMetricId,
    ownOffset,
    ownLimit,
    otherOffset,
    otherLimit,
    sourcePage,
    skipSourceRecords = false,
    recordsRpc
  }: {
    context: TargetContributingRecordsContext
    requestedMetricId?: number
    ownOffset: number
    ownLimit: number
    otherOffset: number
    otherLimit: number
    sourcePage?: TargetContributingSourceRecordsPageParams
    skipSourceRecords?: boolean
    recordsRpc: Pick<
      DolfinRpc,
      | 'getMetricsValues'
      | 'getTargetContributingRecords'
      | 'getTargetTeamIds'
      | 'getMetricsByIdsForPeriod'
      | 'getMetricSourceRecords'
      | 'getOrganizationMetricSourceRecordsPage'
      | 'getProfilesByIds'
      | 'getPeriodTargetAchievementWithEvidence'
      | 'getCreditedMetricValues'
      | 'getTeamMembershipsInPeriod'
    >
  }): Promise<TargetContributingRecordsResult> {
    const { target, organizationId, periodStart, periodEnd, targetAchievement } = context
    const effectiveMetricId = requestedMetricId ?? target.metrics.id

    if (target.metrics.source && effectiveMetricId !== target.metrics.id) {
      throw new TargetContributingRecordsDomainError(
        'not_found',
        'Metric is not available in this target context'
      )
    }

    const metricGraph = target.metrics.source
      ? null
      : await resolveMetricDependencyGraph([target.metrics.id], metricIds =>
          recordsRpc.getMetricsByIdsForPeriod(metricIds, organizationId, periodStart)
        )
    const requestedMetric = target.metrics.source
      ? (
          await recordsRpc.getMetricsByIdsForPeriod(
            [target.metrics.id],
            organizationId,
            periodStart
          )
        ).find(metric => metric.id === target.metrics.id)
      : metricGraph?.metricsById.get(effectiveMetricId)

    if (
      (metricGraph &&
        (metricGraph.hasCycle ||
          metricGraph.missingMetricIds.size > 0 ||
          !metricGraph.reachableMetricIds.has(effectiveMetricId))) ||
      !requestedMetric
    ) {
      throw new TargetContributingRecordsDomainError(
        'not_found',
        'Metric is not available in this target context'
      )
    }

    const targetTeamIds = await recordsRpc.getTargetTeamIds(target.id, organizationId)
    const isCompanyTarget = target.company_target === true
    let teamIds: number[] | undefined

    if (target.global_target) {
      teamIds = targetTeamIds
    } else if (target.team_target) {
      if (context.effectiveTeamId == null || !targetTeamIds.includes(context.effectiveTeamId)) {
        throw new TargetContributingRecordsDomainError(
          'not_found',
          'Target team is not active for this profile'
        )
      }
      teamIds = [context.effectiveTeamId]
    }

    const canViewAllRecords =
      context.isAdmin || target.contributing_records_visibility === 'all_records'
    const recordDetailScope = canViewAllRecords ? 'all' : 'own'
    const recordProfileId = canViewAllRecords ? context.displayedProfileId : context.requesterId
    const setupSelected = target.credit_setup_id != null
    let creditedTeamIds = target.company_target || target.global_target ? targetTeamIds : teamIds
    let creditedTeamMemberships: {
      teamId: number
      effectiveFrom: string
      effectiveTo: string | null
    }[] = []

    if (setupSelected && targetTeamIds.length > 0) {
      const memberships = await recordsRpc.getTeamMembershipsInPeriod({
        end: periodEnd,
        organizationId,
        start: periodStart,
        teamIds: targetTeamIds
      })
      creditedTeamMemberships = memberships
        .filter(
          membership =>
            membership.profileId === context.displayedProfileId &&
            (creditedTeamIds == null || creditedTeamIds.includes(membership.teamId))
        )
        .map(membership => ({
          effectiveFrom: membership.activeFrom,
          effectiveTo: membership.activeUntil,
          teamId: membership.teamId
        }))

      if (target.company_target || target.global_target) {
        creditedTeamIds = [...new Set(creditedTeamMemberships.map(membership => membership.teamId))]
      }
    }

    const sharedCreditedContext =
      target.company_target || target.global_target || target.team_target
    const creditedRequests = setupSelected
      ? [
          {
            organizationId,
            metricId: target.metrics.id,
            creditSetupId: target.credit_setup_id!,
            callSite: 'target_contributing_records',
            profileId: context.displayedProfileId,
            teamIds: creditedTeamIds ?? [],
            teamMemberships: creditedTeamMemberships,
            valueProfileIds: null,
            valueTeamIds: undefined,
            startDate: periodStart,
            endDate: periodEnd
          },
          ...(sharedCreditedContext
            ? [
                {
                  organizationId,
                  metricId: target.metrics.id,
                  creditSetupId: target.credit_setup_id!,
                  callSite: 'target_contributing_records',
                  profileId: context.displayedProfileId,
                  teamIds: creditedTeamIds ?? [],
                  teamMemberships: creditedTeamMemberships,
                  valueProfileIds: [context.displayedProfileId],
                  valueTeamIds: target.company_target ? undefined : teamIds,
                  startDate: periodStart,
                  endDate: periodEnd
                }
              ]
            : [])
        ]
      : []
    const creditedRows = setupSelected
      ? await recordsRpc.getCreditedMetricValues(creditedRequests)
      : []
    const creditedContribution = setupSelected
      ? creditedRows.filter(value => Number(value.request_id) === 0)
      : null
    const displayedCreditedContribution = setupSelected
      ? sharedCreditedContext
        ? creditedRows.filter(value => Number(value.request_id) === 1)
        : creditedContribution
      : null
    const creditedContributionDetails =
      setupSelected && canViewAllRecords
        ? ((
            await recordsRpc.getPeriodTargetAchievementWithEvidence(
              target.id,
              periodStart,
              periodEnd,
              context.displayedProfileId
            )
          )?.creditedEvidence ?? [])
        : undefined
    const creditedEvidence: TargetCreditedEvidenceSummary[] = []
    if (setupSelected) {
      const evidenceByConsumer = new Map<string, TargetCreditedEvidenceSummary>()
      const evidenceValues = canViewAllRecords
        ? creditedContribution
        : displayedCreditedContribution

      for (const value of evidenceValues ?? []) {
        const key = [
          value.credit_setup_rule_id,
          value.assignment_id,
          value.profile_id,
          value.team_id
        ].join(':')
        const current = evidenceByConsumer.get(key)
        if (current) {
          current.contributionCount += 1
          continue
        }

        evidenceByConsumer.set(key, {
          creditSetupId: target.credit_setup_id!,
          creditSetupRuleId: value.credit_setup_rule_id,
          assignmentId: value.assignment_id,
          profileId: value.profile_id,
          teamId: value.team_id,
          contributionCount: 1
        })
      }

      if (evidenceByConsumer.size === 0) {
        creditedEvidence.push({
          creditSetupId: target.credit_setup_id!,
          creditSetupRuleId: null,
          assignmentId: null,
          profileId: context.displayedProfileId,
          teamId: null,
          contributionCount: 0
        })
      } else {
        creditedEvidence.push(...evidenceByConsumer.values())
      }
    }

    if (target.global_target && teamIds?.length === 0) {
      return {
        ownRecords: { records: [], count: 0 },
        otherRecords: recordDetailScope === 'all' ? { records: [], count: 0 } : null,
        ownSourceRecords: [],
        otherSourceRecords: recordDetailScope === 'all' ? [] : null,
        allSourceRecords: recordDetailScope === 'all' ? [] : null,
        requesterProfileId: context.requesterId,
        contributionProfileId: context.displayedProfileId,
        displayedProfileContribution: 0,
        remainingContribution: 0,
        requesterContribution: 0,
        otherUsersContribution: 0,
        sharedContribution: 0,
        teamIds,
        metric: requestedMetric,
        targetAchievement,
        recordDetailScope,
        sourceRecordsPage: sourcePage ? { records: [], count: 0 } : undefined,
        creditedEvidence,
        creditedContributionDetails
      }
    }

    const resolvedTargetCurrency = targetAchievement.target_currency ?? undefined
    const emptyPage = { records: [], count: 0 }
    // Credited values do not currently expose source payloads through the
    // batched eligibility RPC. Never fall back to direct source records for a
    // setup-selected Target, since that would display values outside its Rule.
    const loadLegacyRecordPages = !setupSelected && !skipSourceRecords && !sourcePage
    const ownRecords =
      loadLegacyRecordPages && requestedMetric.source
        ? await recordsRpc.getTargetContributingRecords({
            metricId: requestedMetric.id,
            organizationId,
            periodStart,
            periodEnd,
            source: requestedMetric.source,
            profileId: recordProfileId,
            teamIds,
            targetCurrency: resolvedTargetCurrency,
            offset: ownOffset,
            limit: ownLimit
          })
        : emptyPage
    const otherRecords =
      loadLegacyRecordPages && requestedMetric.source && canViewAllRecords
        ? await recordsRpc.getTargetContributingRecords({
            metricId: requestedMetric.id,
            organizationId,
            periodStart,
            periodEnd,
            source: requestedMetric.source,
            excludedProfileId: recordProfileId,
            teamIds,
            targetCurrency: resolvedTargetCurrency,
            offset: otherOffset,
            limit: otherLimit
          })
        : null

    if (sourcePage && recordDetailScope === 'own' && sourcePage.scope !== 'own') {
      throw new TargetContributingRecordsDomainError(
        'forbidden',
        'The requested source-record scope is not available'
      )
    }

    let sourceRecords: MetricSourceRecord[] = []
    let sourceRecordCount = 0
    if (!setupSelected && !skipSourceRecords && requestedMetric.source) {
      if (sourcePage) {
        if (!isCompanyTarget) {
          throw new TargetContributingRecordsDomainError(
            'bad_request',
            'Paginated source records are available only for company targets'
          )
        }

        const page = await recordsRpc.getOrganizationMetricSourceRecordsPage(
          requestedMetric.id,
          periodStart,
          periodEnd,
          organizationId,
          recordProfileId,
          sourcePage,
          resolvedTargetCurrency
        )
        sourceRecords = page.records
        sourceRecordCount = page.count
      } else if (isCompanyTarget) {
        const page = await recordsRpc.getOrganizationMetricSourceRecordsPage(
          requestedMetric.id,
          periodStart,
          periodEnd,
          organizationId,
          recordProfileId,
          {
            scope: recordDetailScope,
            included: true,
            offset: 0,
            limit: 100
          },
          resolvedTargetCurrency
        )
        sourceRecords = page.records
      } else if (typeof recordsRpc.getMetricSourceRecords === 'function') {
        sourceRecords =
          (await recordsRpc.getMetricSourceRecords(
            requestedMetric.id,
            recordProfileId,
            periodStart,
            periodEnd,
            organizationId,
            teamIds,
            resolvedTargetCurrency
          )) ?? []
      }
    }
    const getSourceRecordProfileId = (sourceRecord: (typeof sourceRecords)[number]) => {
      if (isCompanyTarget) {
        const profileId = sourceRecord.source_record?.metric_value_profile_id

        return typeof profileId === 'string' && profileId.length > 0 ? profileId : null
      }

      if (!requestedMetric.source || !requestedMetric.user_field || !sourceRecord.source_record) {
        return null
      }

      try {
        const profileId = getRecordFieldValue(
          requestedMetric.source,
          sourceRecord.source_record as never,
          requestedMetric.user_field
        )

        return typeof profileId === 'string' && profileId.length > 0 ? profileId : null
      } catch {
        return null
      }
    }
    const sourceRecordProfileIds = new Set(
      sourceRecords
        .map(getSourceRecordProfileId)
        .filter((profileId): profileId is string => profileId !== null)
    )
    const profiles =
      sourceRecordProfileIds.size > 0 && typeof recordsRpc.getProfilesByIds === 'function'
        ? ((await recordsRpc.getProfilesByIds(Array.from(sourceRecordProfileIds))) ?? [])
        : []
    const profilesById = new Map(profiles.map(profile => [profile.id, profile]))
    const enrichedSourceRecords = sourceRecords.map(
      (sourceRecord): TargetContributingSourceRecord => {
        const profileId = getSourceRecordProfileId(sourceRecord)

        return {
          ...sourceRecord,
          profile_id: profileId,
          profiles: profileId ? (profilesById.get(profileId) ?? null) : null
        }
      }
    )
    const ownSourceRecords = enrichedSourceRecords.filter(
      sourceRecord => sourceRecord.profile_id === recordProfileId
    )
    const otherSourceRecords = enrichedSourceRecords.filter(
      sourceRecord => sourceRecord.profile_id !== recordProfileId
    )

    let displayedProfileContribution: number
    let sharedContribution: number

    if (setupSelected) {
      displayedProfileContribution = aggregateCreditedMetricValues(
        displayedCreditedContribution ?? [],
        target.metrics.aggregate_function
      )
      sharedContribution = aggregateCreditedMetricValues(
        creditedContribution ?? [],
        target.metrics.aggregate_function
      )
    } else {
      const [displayedProfileMetrics, sharedMetrics] = await Promise.all([
        recordsRpc.getMetricsValues(
          [effectiveMetricId],
          periodStart,
          periodEnd,
          organizationId,
          [context.displayedProfileId],
          teamIds,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          resolvedTargetCurrency
        ),
        recordsRpc.getMetricsValues(
          [effectiveMetricId],
          periodStart,
          periodEnd,
          organizationId,
          undefined,
          teamIds,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          resolvedTargetCurrency
        )
      ])
      const getContribution = (metrics: Awaited<ReturnType<DolfinRpc['getMetricsValues']>>) =>
        Number(
          metrics.find(metric => metric.metric_id === effectiveMetricId)?.aggregated_value ?? 0
        )
      displayedProfileContribution = getContribution(displayedProfileMetrics)
      sharedContribution = getContribution(sharedMetrics)
    }
    const remainingContribution = sharedContribution - displayedProfileContribution

    return {
      ownRecords,
      otherRecords,
      ownSourceRecords,
      otherSourceRecords: canViewAllRecords ? otherSourceRecords : null,
      allSourceRecords: canViewAllRecords ? enrichedSourceRecords : null,
      requesterProfileId: context.requesterId,
      contributionProfileId: context.displayedProfileId,
      displayedProfileContribution,
      remainingContribution,
      requesterContribution: displayedProfileContribution,
      otherUsersContribution: remainingContribution,
      sharedContribution,
      teamIds,
      metric: requestedMetric,
      targetAchievement,
      recordDetailScope,
      sourceRecordsPage: sourcePage
        ? { records: enrichedSourceRecords, count: sourceRecordCount }
        : undefined,
      creditedEvidence,
      creditedContributionDetails
    }
  }

  async getCompletedTargetAttainmentAnalysis({
    organizationId,
    targetId,
    startDate,
    endDate
  }: {
    organizationId: number
    targetId?: number
    startDate: string
    endDate: string
  }): Promise<CompletedTargetAttainmentAnalysis[]> {
    const facts = await this.rpcClient.getOrganizationCompletedTargetAttainment(
      organizationId,
      startDate,
      endDate,
      targetId
    )
    const versionIds = Array.from(
      new Set(facts.flatMap(fact => fact.applicable_rule_version_ids ?? []))
    )
    const versionsById = new Map(
      (await this.rpcClient.getCompensationRuleVersionsForAnalysis(organizationId, versionIds)).map(
        version => [version.version_id, version]
      )
    )
    const factsByCohort = new Map<string, CompletedTargetAttainmentFact[]>()

    for (const fact of facts) {
      const key = [fact.target_id, fact.period_start, fact.period_end, fact.target_scope].join(':')
      const cohort = factsByCohort.get(key)
      if (cohort) {
        cohort.push(fact)
      } else {
        factsByCohort.set(key, [fact])
      }
    }

    return Array.from(factsByCohort.values()).map(cohort => {
      const representative = cohort[0]
      const eligibleFacts = cohort.filter(
        fact =>
          fact.data_status === 'eligible' &&
          Number.isFinite(fact.attainment) &&
          fact.participant_id !== null
      )
      const sampleSize = eligibleFacts.length
      const attainments = eligibleFacts.map(fact => fact.attainment)
      const hasSufficientEvidence =
        representative.data_status === 'eligible' && sampleSize >= minimumDisclosureCohortSize
      const p25 = hasSufficientEvidence ? percentile(attainments, 0.25) : null
      const median = hasSufficientEvidence ? percentile(attainments, 0.5) : null
      const p75 = hasSufficientEvidence ? percentile(attainments, 0.75) : null
      const quotaAttainmentRate = hasSufficientEvidence
        ? (attainments.filter(attainment => attainment >= 1).length / sampleSize) * 100
        : null
      const lowerTailRate = hasSufficientEvidence
        ? (attainments.filter(attainment => attainment < 0.7).length / sampleSize) * 100
        : null
      const upperTailRate = hasSufficientEvidence
        ? (attainments.filter(attainment => attainment > 1.5).length / sampleSize) * 100
        : null

      let quotaSignal: QuotaSignal = 'unsupported'
      if (representative.data_status === 'eligible' && !hasSufficientEvidence) {
        quotaSignal = 'insufficient_evidence'
      } else if (hasSufficientEvidence && median != null && quotaAttainmentRate != null) {
        if (median < 0.85 || quotaAttainmentRate < 30 || (lowerTailRate ?? 0) >= 30) {
          quotaSignal = 'quota_may_be_aggressive'
        } else if (median >= 1.15 || quotaAttainmentRate >= 70 || (upperTailRate ?? 0) >= 15) {
          quotaSignal = 'quota_may_be_easy'
        } else {
          quotaSignal = 'within_configured_range'
        }
      }

      const candidatesByVersionAndTarget = new Map<number, CurveCandidate[]>()
      const assignments = eligibleFacts.flatMap(fact => {
        const candidateVersionIds = fact.applicable_rule_version_ids ?? []
        const candidates = candidateVersionIds.flatMap(versionId => {
          const cached = candidatesByVersionAndTarget.get(versionId)
          if (cached) return cached

          const version = versionsById.get(versionId)
          const resolvedCandidates = version
            ? getTargetTierCandidates(version.diagram, fact.target_id, versionId)
            : []
          candidatesByVersionAndTarget.set(versionId, resolvedCandidates)
          return resolvedCandidates
        })

        return candidates.length === 1 ? [{ fact, candidate: candidates[0] }] : []
      })
      const assignmentsByCurve = new Map<
        string,
        Array<{ fact: CompletedTargetAttainmentFact; candidate: CurveCandidate }>
      >()

      for (const assignment of assignments) {
        const key = [
          assignment.candidate.versionId,
          assignment.candidate.nodeId,
          assignment.candidate.payoutUnit
        ].join(':')
        const curveAssignments = assignmentsByCurve.get(key)
        if (curveAssignments) {
          curveAssignments.push(assignment)
        } else {
          assignmentsByCurve.set(key, [assignment])
        }
      }

      const curveCohorts: CompletedTargetCurveCohort[] = Array.from(
        assignmentsByCurve.values()
      ).map(curveAssignments => {
        const { candidate } = curveAssignments[0]
        const curveSampleSize = curveAssignments.length
        const metrics = getPayCurveMetrics(candidate.config)
        const hasResolvedPayouts = metrics.points.length === candidate.config.points.length
        const curveHasSufficientEvidence = curveSampleSize >= minimumDisclosureCohortSize
        const curveStatus: CompletedTargetCurveCohort['curveStatus'] = !hasResolvedPayouts
          ? 'unresolved_payout_formula'
          : curveHasSufficientEvidence
            ? 'eligible'
            : 'insufficient_evidence'
        const curveAttainments = curveAssignments.map(({ fact }) =>
          candidate.capTargetAchievement
            ? Math.min(fact.attainment * 100, 100)
            : fact.attainment * 100
        )
        const rateAtOrAbove = (boundary: number | null) =>
          boundary == null || curveStatus !== 'eligible'
            ? null
            : (curveAttainments.filter(attainment => attainment >= boundary).length /
                curveSampleSize) *
              100

        return {
          ruleVersionId: candidate.versionId,
          nodeId: candidate.nodeId,
          payoutUnit: candidate.payoutUnit,
          sampleSize: curveSampleSize,
          coverageRate: (curveSampleSize / sampleSize) * 100,
          curveStatus,
          thresholdAttainment: curveStatus === 'eligible' ? metrics.threshold : null,
          acceleratorAttainment:
            curveStatus === 'eligible' ? metrics.highestAccelerationStart : null,
          capAttainment: curveStatus === 'eligible' ? metrics.capAttainment : null,
          thresholdHitRate: rateAtOrAbove(metrics.threshold),
          acceleratorParticipationRate: rateAtOrAbove(metrics.highestAccelerationStart),
          capParticipationRate: rateAtOrAbove(metrics.capAttainment)
        }
      })
      const curveEligibilityCoverage = hasSufficientEvidence
        ? (assignments.length / sampleSize) * 100
        : null
      const governanceStatus = !hasSufficientEvidence
        ? 'not_evaluated'
        : curveCohorts.length === 0
          ? 'no_applicable_curve'
          : curveCohorts.length > 1 || assignments.length !== sampleSize
            ? 'multiple_applicable_curves'
            : 'single_applicable_curve'

      return {
        targetId: representative.target_id,
        targetLabel: representative.target_label,
        periodStart: representative.period_start,
        periodEnd: representative.period_end,
        targetType: representative.target_type,
        targetScope: representative.target_scope,
        dataStatus: representative.data_status as CompletedTargetAttainmentDataStatus,
        sampleSize,
        eligibleParticipantCount: representative.eligible_participant_count,
        excludedParticipantCount: representative.excluded_participant_count,
        p25Attainment: p25 == null ? null : p25 * 100,
        medianAttainment: median == null ? null : median * 100,
        p75Attainment: p75 == null ? null : p75 * 100,
        quotaAttainmentRate,
        lowerTailRate,
        upperTailRate,
        quotaSignal,
        governanceStatus,
        curveEligibilityCoverage,
        curveCohorts,
        scoreVersion: null,
        scoreComponents: null
      }
    })
  }

  static computeTargetAchievement(
    target: number,
    isUpperBound: boolean,
    allowPartial: boolean,
    value: number
  ) {
    if (target === 0) return 0

    if (isUpperBound) {
      if (allowPartial && value !== 0) {
        return target / value
      }

      if (allowPartial && value === 0) {
        return Number.POSITIVE_INFINITY
      }

      return value <= target ? 1 : 0
    }

    return value / target
  }
}
